/**
 * Voice transport: microphone -> xAI realtime WebSocket -> tool-call proposals.
 *
 * The browser connects to xAI DIRECTLY. The server mints a single-use ephemeral
 * token and hands back a ready-made `session` config; this file is deliberately
 * dumb about the contents of that config. It forwards it verbatim as
 * `session.update` because the tool definitions inside it are generated from the
 * server's command registry, and retyping them here is exactly how the model's
 * contract and the server's validation drift apart.
 *
 * THE SAFETY PROPERTY, stated once: nothing in this file applies anything. A
 * tool call from the model produces a PREVIEW and an `onEvent("proposal")`. The
 * caller shows it to a person, and only a person's tap can reach apply. There is
 * no code path from a socket message to a write, and there must never be one —
 * a model that can talk itself into a confirmation is a model that can delete a
 * freezer box nobody agreed to delete.
 *
 * Audio is 24 kHz PCM16 mono, produced in an AudioWorklet and base64'd onto the
 * socket. The shape of this is taken from the measured probe implementation.
 */

// Emitted in ~20 ms frames by the worklet. Also carries an RMS level, computed
// there rather than on the main thread so the meter cannot cost frames.
const WORKLET_SOURCE = `
  class PcmProcessor extends AudioWorkletProcessor {
    process(inputs) {
      const channel = inputs[0][0];
      if (channel) {
        const pcm = new Int16Array(channel.length);
        let sumSquares = 0;
        for (let i = 0; i < channel.length; i++) {
          const clamped = Math.max(-1, Math.min(1, channel[i]));
          sumSquares += clamped * clamped;
          pcm[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
        }
        const rms = Math.sqrt(sumSquares / channel.length);
        this.port.postMessage({ buffer: pcm.buffer, rms }, [pcm.buffer]);
      }
      return true;
    }
  }
  registerProcessor('sa-pcm', PcmProcessor);
`;

// A session that has gone quiet is still billing. The server deliberately does
// not set idle_timeout_ms, because that setting re-engages and re-bills a dead
// session; closing it is the client's job, so it is done here.
const SILENCE_TIMEOUT_MS = 60000;

// Trailing-silence endpointing, measured from the audio we are already
// analysing rather than from a clock. The worklet computes an RMS per ~20 ms
// frame for the meter; this reuses it.
//
// Why this rather than the fixed ceiling alone: a ceiling ends every session at
// the same moment whether the user finished in four seconds or is still
// talking. Silence ends it when they ACTUALLY stopped, which is both cheaper and
// less rude. The sibling LAN product endpoints on 1.3s of trailing silence for
// commands; we allow longer because our whole turn (speech -> transcription ->
// tool call) rides one connection and a mid-sentence pause must not end it.
//
// The threshold is deliberately low: this is "no speech", not "quiet room".
// Ambient noise sits well under it, which is the point — VAD's speech_started
// fires on room noise and that is exactly what defeated the old idle timer.
const ENDPOINT_RMS_THRESHOLD = 0.012;

// Frames captured before the socket is ready are HELD, not dropped.
//
// The browser marks the microphone live the instant permission is granted, so
// the user starts talking then — but capture used to begin inside `onopen`,
// after an AudioContext, a worklet module load and a WebSocket handshake. On a
// phone that is easily a second or two, and every word spoken in it was never
// captured at all: the audio graph was not yet connected. xAI's own guidance is
// to buffer early microphone audio while the socket opens.
//
// Bounded, because an unbounded buffer on a socket that never opens is a leak.
// 150 frames at ~20 ms is about three seconds — comfortably longer than the gap
// and short enough that a stalled connection cannot grow it without limit.
const PREBUFFER_MAX_FRAMES = 150;
const ENDPOINT_SILENCE_MS = 3000;
// Backstop for a session that is never quiet — a noisy lab, a stuck stream. The
// server cannot cut a session off mid-flight without a proxy, so this is the
// only ceiling that exists, which is exactly why it should be tight.
//
// Was ten minutes. A forgotten session on a bench therefore billed for ten
// minutes, and the idle timeout could not save it because VAD re-armed on room
// noise (see handleServerEvent). Ninety seconds is generous for "move box
// BOX-03 to KIM48 well 7" — the longest real session measured was 33s — and the
// user can simply start another. The sibling LAN product reached the same
// conclusion from the other direction: press-to-talk with a 60s absolute,
// non-resettable ceiling. Bounding the worst case matters more than never
// interrupting anyone.
const MAX_SESSION_MS = 90 * 1000;

function base64FromPcm(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  // Chunked: a single spread over a long buffer overflows the argument list.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + CHUNK, bytes.length))
    );
  }
  return btoa(binary);
}

export default class VoiceSession {
  /**
   * @param {object} options
   * @param {object} options.credentials  the `data` block from POST /voice/session-token
   * @param {function} options.onEvent    (type, payload) => void
   */
  constructor({ credentials, onEvent }) {
    this.credentials = credentials;
    this.onEvent = onEvent || (() => {});

    this.socket = null;
    this.audioContext = null;
    this.micStream = null;
    this.workletNode = null;
    this.sourceNode = null;

    this.closed = false;
    // Set while a proposal is waiting for a human. Audio keeps being captured
    // but stops being forwarded, so the model cannot stack a second proposal on
    // top of the one the user is currently reading and quietly replace it.
    this.muted = false;
    this.startedAt = null;
    // Names arrive on one event and arguments on another; this keeps them
    // together per call so a proposal is never raised with the wrong intent.
    this.pendingCalls = {};
    this.outcome = { commandsPreviewed: 0, commandsApplied: 0, refusals: 0 };

    this.silenceTimer = null;
    this.maxTimer = null;
    // Trailing-silence endpointing state. `heardSpeech` gates it so that leading
    // silence — the pause between opening the mic and starting to talk — never
    // ends the session; only silence AFTER speech counts as "finished".
    // Audio captured before the socket was ready, flushed on open.
    this.preBuffer = [];
    this.socketReady = false;
    this.heardSpeech = false;
    this.quietSince = null;
    // Endpointing waits for a COMPLETED turn, not merely for speech. Someone
    // reading a serial off a frosted rack pauses mid-sentence, and ending the
    // session on that would cut off the command they were halfway through
    // saying. A completed transcription means the model has taken a full turn,
    // so silence after it means "finished", not "thinking".
    this.sawTranscript = false;
    // True from the moment a transcript lands until the model has finished
    // answering it. THIS is the window the endpoint detector must not fire in.
    //
    // `pendingCalls` was supposed to cover it and does not: it only becomes
    // non-empty when `response.output_item.added` arrives, so the gap between
    // "the user stopped talking" and "the tool call starts arriving" is
    // unguarded — and that gap is exactly when the user is silent, because they
    // have finished speaking. Measured in production: four consecutive sessions
    // ran 20-25s and produced ZERO tool calls, because each was killed three
    // seconds after the transcript and the answer landed on a dead socket.
    this.awaitingResponse = false;
  }

  /** Seconds of wall-clock session time, for the display-only usage report. */
  elapsedSeconds() {
    if (!this.startedAt) return 0;
    return Math.round((Date.now() - this.startedAt) / 1000);
  }

  emit(type, payload) {
    if (this.closed && type != "closed") return;
    this.onEvent(type, payload);
  }

  /**
   * Opens the microphone, then the socket. Microphone first, deliberately: a
   * denied permission must not burn the single-use token that was already
   * minted, and it is the failure the user is most likely to hit.
   */
  async start() {
    await this.openMicrophone();
    // Checked again between the two awaits: a close during the socket handshake
    // has the same shape as a close during the permission prompt.
    if (this.closed) {
      try {
        this.micStream?.getTracks().forEach(t => t.stop());
      } catch {
        /* already released */
      }
      this.micStream = null;
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }
    await this.openSocket();
    if (this.closed) {
      // Socket opened onto a dead session. Close it explicitly — an open realtime
      // socket bills whether or not anyone is listening to it.
      try {
        this.socket?.close();
      } catch {
        /* already gone */
      }
      try {
        this.micStream?.getTracks().forEach(t => t.stop());
      } catch {
        /* already released */
      }
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }
    this.startedAt = Date.now();
    this.armSilenceTimer();
    this.maxTimer = setTimeout(() => {
      this.stop("timeout");
    }, MAX_SESSION_MS);
  }

  async openMicrophone() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw Object.assign(new Error("unsupported"), { voiceClass: "unsupported" });
    }

    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      // Denied, dismissed, or no device. All three mean the same thing to the
      // user and get the same calm sentence; none of them is a broken system.
      throw Object.assign(new Error("microphone"), { voiceClass: "micDenied" });
    }

    // The permission prompt can sit on screen indefinitely, and the user may
    // close the modal while it is up. `stop()` will already have run by then,
    // found micStream still null, and set `closed` — so it had nothing to release
    // AND it can never release anything again, because stop() returns early once
    // closed. The stream we just received would stay live with nothing owning it.
    //
    // Release it here, at the only moment it is reachable.
    if (this.closed) {
      try {
        this.micStream.getTracks().forEach(t => t.stop());
      } catch {
        /* nothing to release */
      }
      this.micStream = null;
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }

    this.audioContext = new AudioContext({ sampleRate: 24000 });
    const moduleUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: "application/javascript" })
    );
    try {
      await this.audioContext.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }

    // The worklet load is another await the modal can be closed during. Check
    // again rather than connecting a live microphone to a dead session.
    if (this.closed) {
      try {
        this.micStream?.getTracks().forEach(t => t.stop());
      } catch {
        /* already released */
      }
      this.micStream = null;
      throw Object.assign(new Error("aborted"), { voiceClass: "aborted" });
    }

    // Start capturing NOW, before the socket exists. Frames are buffered until
    // it opens. This is what stops the first words of a command being lost.
    this.startStreamingAudio();
  }

  openSocket() {
    return new Promise((resolve, reject) => {
      const { url, token } = this.credentials;
      let settled = false;

      this.socket = new WebSocket(url, [`xai-client-secret.${token}`]);

      this.socket.onopen = () => {
        settled = true;
        try {
          // Verbatim. See the file header: this object carries tool definitions
          // generated from the server's command registry.
          this.send({ type: "session.update", session: this.credentials.session });
          // Capture is already running; release what was said while we connected.
          this.socketReady = true;
          this.flushPreBuffer();
        } catch (err) {
          // Without this the promise hangs FOREVER on any audio-setup failure:
          // `settled` is already true, so neither onerror nor onclose will
          // reject it, and the caller's cleanup never runs. The microphone stays
          // live and the upstream session keeps billing with nothing owning it.
          // AudioWorklet construction is the realistic thrower here.
          this.stop("start-failed");
          reject(
            Object.assign(new Error("transport"), { voiceClass: "transport" })
          );
          return;
        }
        this.emit("listening");
        resolve();
      };

      this.socket.onmessage = (ev) => {
        let event;
        try {
          event = JSON.parse(ev.data);
        } catch {
          return;
        }
        this.handleServerEvent(event);
      };

      this.socket.onerror = () => {
        if (!settled) {
          settled = true;
          reject(
            Object.assign(new Error("transport"), { voiceClass: "transport" })
          );
        }
      };

      this.socket.onclose = () => {
        if (!settled) {
          settled = true;
          reject(
            Object.assign(new Error("transport"), { voiceClass: "transport" })
          );
          return;
        }
        // An unrequested close mid-session is a dropped connection, which is
        // `danger` — the user's words went nowhere and they need to know.
        if (!this.closed) this.stop("transport-lost");
      };
    });
  }

  startStreamingAudio() {
    this.sourceNode = this.audioContext.createMediaStreamSource(this.micStream);
    this.workletNode = new AudioWorkletNode(this.audioContext, "sa-pcm");

    this.workletNode.port.onmessage = ({ data }) => {
      // The meter is driven from here and only from here, so it reflects real
      // captured audio rather than elapsed time (DESIGN.md section 10). While
      // muted it reads zero, which is the truth: nothing is being sent.
      this.emit("level", this.muted ? 0 : data.rms);
      if (this.muted) return;
      this.observeLevel(data.rms);
      const encoded = base64FromPcm(data.buffer);
      if (!this.socketReady || !this.socket || this.socket.readyState != 1) {
        // Hold it. Oldest frames go first if the connection is slow enough to
        // overrun the window — losing the start of a long wait is better than
        // growing without bound.
        this.preBuffer.push(encoded);
        if (this.preBuffer.length > PREBUFFER_MAX_FRAMES) this.preBuffer.shift();
        return;
      }
      this.send({ type: "input_audio_buffer.append", audio: encoded });
    };

    this.sourceNode.connect(this.workletNode);
    // Deliberately NOT connected to destination: routing the microphone to the
    // speakers would put the user's own voice in their ear.
  }

  send(payload) {
    if (!this.socket || this.socket.readyState != 1) return;
    try {
      this.socket.send(JSON.stringify(payload));
    } catch {
      // A send that fails on a socket reporting OPEN means the transport is
      // going away; onclose will report it.
    }
  }

  /**
   * Decide, from the audio itself, whether the speaker has finished.
   *
   * Only runs while unmuted, and only ends the session once speech has actually
   * been heard AND nothing is in flight — a tool call arrives AFTER the user
   * stops talking, so ending on silence alone would cut off the very answer the
   * session exists to collect.
   */
  observeLevel(rms) {
    if (rms >= ENDPOINT_RMS_THRESHOLD) {
      this.heardSpeech = true;
      this.quietSince = null;
      return;
    }
    if (!this.heardSpeech || !this.sawTranscript) return;

    // Never end while the model is answering, or about to.
    if (this.awaitingResponse || Object.keys(this.pendingCalls).length > 0) {
      this.quietSince = null;
      return;
    }

    const now = Date.now();
    if (this.quietSince == null) {
      this.quietSince = now;
      return;
    }
    if (now - this.quietSince >= ENDPOINT_SILENCE_MS) {
      this.stop("idle");
    }
  }

  /** Send everything captured while the socket was still connecting. */
  flushPreBuffer() {
    if (!this.preBuffer.length) return;
    const held = this.preBuffer;
    this.preBuffer = [];
    held.forEach(audio => this.send({ type: "input_audio_buffer.append", audio }));
  }

  armSilenceTimer() {
    clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => {
      this.stop("idle");
    }, SILENCE_TIMEOUT_MS);
  }

  handleServerEvent(event) {
    switch (event.type) {
      case "input_audio_buffer.speech_started":
        // Deliberately does NOT re-arm the idle timer. This is server-side VAD,
        // which fires on any sound over threshold — a centrifuge, a conversation
        // across the room, a door. Re-arming here meant a session in a working
        // lab never went idle and simply ran to the ceiling, billing throughout.
        // The idle timer is re-armed by TRANSCRIPTION and by tool calls instead:
        // both require the model to have actually recognised something, which is
        // the difference between "someone is talking to us" and "it is noisy".
        this.emit("listening", { speaking: true });
        break;

      case "input_audio_buffer.speech_stopped":
        this.emit("thinking");
        break;

      case "conversation.item.input_audio_transcription.completed":
        this.sawTranscript = true;
        this.quietSince = null;
        // A transcript means an answer is coming. Hold the endpoint detector
        // until it arrives or the response completes.
        this.awaitingResponse = true;
        this.armSilenceTimer();
        this.emit("transcript", event.transcript || "");
        break;

      // The name and the call id arrive here; the arguments arrive separately.
      case "response.output_item.added":
      case "response.output_item.done": {
        const item = event.item || {};
        if (item.type == "function_call" && item.call_id) {
          this.pendingCalls[item.call_id] = {
            name: item.name || this.pendingCalls[item.call_id]?.name,
            arguments: item.arguments || this.pendingCalls[item.call_id]?.arguments,
          };
        }
        break;
      }

      case "response.done":
      case "response.completed":
        // The model has finished with that turn, whatever it produced. Silence
        // from here genuinely means the user has stopped.
        this.awaitingResponse = false;
        this.quietSince = null;
        break;

      case "response.function_call_arguments.done": {
        const callId = event.call_id || event.item_id;
        const known = this.pendingCalls[callId] || {};
        const name = event.name || known.name;
        this.armSilenceTimer();
        this.raiseProposal(callId, name, event.arguments || known.arguments);
        break;
      }

      case "response.created":
        this.emit("thinking");
        break;

      case "response.done":
        // Back to open-mic. Not "idle": the socket is live and billing.
        this.emit("listening");
        break;

      case "error":
        this.emit("model-error", event.error || {});
        break;

      default:
        break;
    }
  }

  /**
   * A tool call became a proposal. This raises an event and stops; it does not
   * call preview itself, because the caller owns the HTTP layer and the store.
   */
  raiseProposal(callId, intentName, rawArguments) {
    if (!intentName) return;
    let fields;
    try {
      fields = JSON.parse(rawArguments || "{}");
    } catch {
      fields = null;
    }
    if (!fields) {
      // The model produced something unparseable. Tell it so rather than
      // silently dropping the turn, or it waits forever for a tool result.
      this.respondToTool(callId, "Could not read that request. Ask again.");
      return;
    }

    this.outcome.commandsPreviewed += 1;
    this.emit("proposal", { callId, intent: intentName, fields });
  }

  /**
   * Sends a tool result back so the conversation can continue, then asks for a
   * short spoken acknowledgement. The wording always states that nothing has
   * happened yet — the model must never believe it has applied anything, or it
   * will tell the user so while the confirm button is still sitting there.
   */
  respondToTool(callId, resultText) {
    if (!callId) return;
    delete this.pendingCalls[callId];
    this.send({
      type: "conversation.item.create",
      item: {
        type: "function_call_output",
        call_id: callId,
        output: JSON.stringify({ applied: false, status: resultText }),
      },
    });
    this.send({ type: "response.create" });
  }

  /**
   * Stops forwarding captured audio without tearing the session down. Used while
   * a proposal is on screen awaiting a human, and while an apply is in flight.
   *
   * The silence timer is suspended for the duration: a person reading a delete
   * confirmation is not idle, and closing the transport under them would replace
   * the thing they are deciding about with a dropped-connection error. The hard
   * MAX_SESSION_MS ceiling still applies.
   */
  setMuted(muted) {
    this.muted = !!muted;
    if (this.muted) clearTimeout(this.silenceTimer);
    else this.armSilenceTimer();
  }

  noteApplied() {
    this.outcome.commandsApplied += 1;
  }

  noteRefusal() {
    this.outcome.refusals += 1;
  }

  /** Idempotent. Safe to call from an error path and again from unmount. */
  stop(reason) {
    if (this.closed) return;
    this.closed = true;

    clearTimeout(this.silenceTimer);
    clearTimeout(this.maxTimer);

    const seconds = this.elapsedSeconds();

    try {
      if (this.workletNode) {
        this.workletNode.port.onmessage = null;
        this.workletNode.disconnect();
      }
    } catch {
      /* already torn down */
    }
    try {
      if (this.sourceNode) this.sourceNode.disconnect();
    } catch {
      /* already torn down */
    }
    try {
      if (this.micStream) this.micStream.getTracks().forEach((t) => t.stop());
    } catch {
      /* already torn down */
    }
    try {
      if (this.audioContext && this.audioContext.state != "closed") {
        this.audioContext.close();
      }
    } catch {
      /* already torn down */
    }
    try {
      if (this.socket) {
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket.onmessage = null;
        this.socket.close();
      }
    } catch {
      /* already torn down */
    }

    this.onEvent("closed", { reason, seconds, outcome: this.outcome });
  }
}
